import moment from "moment";
import React, { useMemo } from "react";
import { ReservationRequestDetailsModel, ReservationAmenityModel, HourlyRequestToBookStatus } from "Services/API/API";
import { format, formatReservationTime, formatTime } from "Utils/datetime";
import classes from "./HourlyRequest.module.scss";
import { InputFL } from "Components/Inputs";
import { UseFormReturn } from "react-hook-form";
import { EditHourlyRequest } from "./AdminDetailsModal";
import { useSelector } from "react-redux";
import { userSelector } from "Utils/selectors";
import ModuleCaptionTooltip from "Components/Module/ModuleCaptionTooltip";

interface CommonDetailsProps {
   request: ReservationRequestDetailsModel | undefined
   formMethods?: UseFormReturn<EditHourlyRequest>;
   isLoading?: boolean;
}

export const CommonDetails: React.FC<CommonDetailsProps> = ({ request, formMethods, isLoading }) => {
   const user = useSelector(userSelector);

   const requestedAmenities = useMemo<ReservationAmenityModel[]>(() => {
      if (request?.amenities) {
         return request.amenities.filter(a => a.requestable);
      }
      return [];
   }, [request]);

   const includedAmenities = useMemo<ReservationAmenityModel[]>(() => {
      if (request?.amenities) {
         return request.amenities.filter(a => !a.requestable);
      }
      return [];
   }, [request]);
   
   return (
      <div className={classes.wrapper}>
         <div className="vg-table-wrapper">
            <table className={classes.table}>
               <tbody>
                  <tr>
                     <td>Received:</td>
                     <td>{request?.createTime ? `${moment(request.createTime).format("MMM D, YYYY")} at ${formatTime(request.createTime)}` : "N/A"}</td>
                  </tr>
                  <tr>
                     <td>Space:</td>
                     <td>{request?.workspaceName} at {formMethods && request?.venueId ? <><a href={`/managevenue/${request.venueId}/settings`} target="_blank">{request?.venueName}</a></> : request?.venueName}</td>
                  </tr>
                  <tr>
                     <td>Address:</td>
                     <td>{request?.address}</td>
                  </tr>
                  <tr>
                     <td className="text-nowrap">Date & Time:</td>
                     <td>
                        <div>{format(request?.startTime, "dddd, MMM DD, YYYY")}</div>
                        <div>{formatReservationTime(request?.startTime, request?.endTime)}</div>
                        {!!request?.recurrencePatternText && <div>{request.recurrencePatternText}</div>}
                     </td>
                  </tr>
                  <tr>
                     <td>Organizer:</td>
                     <td>{request?.owner?.firstName} {request?.owner?.lastName}{request?.owner?.enterpriseAccountName ? `, ${request.owner.enterpriseAccountName}` : ""}</td>
                  </tr>
                  {formMethods && user.isSuperUser && <tr>
                     <td>Email (SU):</td>
                     <td>
                        {
                           request?.owner?.id
                              ? <a href={`/m/${request.owner.id}/profile`} target="_blank">{request.owner.email}</a>
                              : request?.owner?.email
                        }
                     </td>
                  </tr>}
                  {!!request?.inviteesNamesString &&
                     <tr>
                        <td>Attendees:</td>
                        <td>{request?.inviteesNamesString}</td>
                     </tr>
                  }
                  <tr>
                     <td>Payment:</td>
                     <td>{request?.payment || "-"}</td>
                  </tr>
                  <tr>
                     <td>Amenities:</td>
                     <td>{requestedAmenities.concat(includedAmenities).map(a => a.name).join(", ") || "-"}</td>
                  </tr>
                  {
                     formMethods && (request?.status == HourlyRequestToBookStatus.Unknown || request?.status == HourlyRequestToBookStatus.Pending || request?.status == HourlyRequestToBookStatus.Paused)
                        ? <tr>
                           <td className={classes.messageLabel}>Add message to guest:</td>
                           <td>
                              <InputFL type="textarea" autoSize control={formMethods.control} name="message" disabled={isLoading} />
                           </td>
                        </tr>
                        : null
                  }
                  {formMethods && user.isSuperUser && (request?.status == HourlyRequestToBookStatus.Unknown || request?.status == HourlyRequestToBookStatus.Pending || request?.status == HourlyRequestToBookStatus.Paused) && <tr>
                     <td className={classes.lsCostCaptionWrapper}>
                        <div className={classes.lsCostCaption}><span>LiquidSpace Cost (SU):</span><ModuleCaptionTooltip>Enter the cost of the space at the time of booking. This will be found on the venue website.</ModuleCaptionTooltip></div>
                     </td>
                     <td>
                        <div className={classes.lsCostInputWrapper}>
                           <div className={classes.currPrefix}>{request?.currencyPrefix}</div>
                           <div><InputFL type="number" className="input-number-clear" control={formMethods.control} name="liquidSpaceCost" placeholder="LiquidSpace Cost" disabled={isLoading} /></div>
                        </div>
                     </td>
                  </tr>}
               </tbody>
            </table>
         </div>
      </div>
   );
}